import {
  PlaybookChapter,
  PhaseData,
  ScheduleData,
  PersonaData,
  PitchData,
  CalloutData,
  KvListData,
  ListData,
} from '@/types/gamePlan';
import { esc, header, footer, openPrintDoc } from './printShared';

const sectionOf = (ch: PlaybookChapter | undefined, title: string) =>
  ch?.sections.find((s) => s.title === title);
const chapterOf = (chapters: PlaybookChapter[], id: string) => chapters.find((c) => c.id === id);

// ---- 16-Week Timeline wall-planner ----
export function printTimeline(chapters: PlaybookChapter[]): void {
  const phasesFr = sectionOf(chapterOf(chapters, 'gameplan'), 'The Timeline')?.fragments ?? [];
  const phases = phasesFr.map((f) => f.data as unknown as PhaseData);
  if (!phases.length) return;

  const ramp = `<div class="ramp">${phases
    .map(
      (p, i) =>
        `<div class="rstep"><div class="rn">${esc(String(p.target))}</div><div class="rr">${esc(
          p.range,
        )}</div></div>${i < phases.length - 1 ? '<div class="rarw">→</div>' : ''}`,
    )
    .join('')}</div>`;

  const cards = phases
    .map(
      (p, i) => `<div class="card phase">
        <div class="ph-top"><span class="ph-n">${i + 1}</span><span class="ph-tgt">${esc(
          String(p.target),
        )} members</span></div>
        <div class="ph-rng">${esc(p.range)}</div>
        <div class="ph-ttl">${esc(p.title)}</div>
        <ul>${(p.focus ?? []).map((x) => `<li><span class="dot"></span>${esc(x)}</li>`).join('')}</ul>
      </div>`,
    )
    .join('');

  const css = `
    .ramp { display:flex; align-items:center; flex-wrap:wrap; gap:10px; margin:18px 0 6px; }
    .rstep { text-align:center; min-width:54px; } .rn { font-size:26px; font-weight:800; color:#B58F2D; line-height:1; }
    .rr { font-size:10px; color:#6C756D; margin-top:3px; } .rarw { color:#B58F2D; font-size:18px; }
    .grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-top:12px; }
    .phase .ph-top { display:flex; justify-content:space-between; align-items:center; }
    .ph-n { width:22px; height:22px; border-radius:50%; background:#0C3D2D; color:#FBF6EC; font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; }
    .ph-tgt { font-size:12px; font-weight:700; color:#7C6220; background:rgba(181,143,45,.12); padding:3px 9px; border-radius:999px; }
    .ph-rng { font-size:11px; letter-spacing:.08em; text-transform:uppercase; color:#6C756D; margin-top:10px; }
    .ph-ttl { font-size:16px; font-weight:700; color:#16241E; margin:2px 0 8px; }
    .phase ul { list-style:none; margin:0; padding:0; } .phase li { display:flex; gap:8px; font-size:12.5px; line-height:1.45; padding:3px 0; color:#16241E; }
    .dot { flex:none; width:6px; height:6px; border-radius:50%; background:#B58F2D; margin-top:7px; }`;

  openPrintDoc(
    'Tee Box — 16-Week Timeline',
    css,
    header('Owner Playbook', '16-Week Game Plan', 'Signing to grand opening — the phase-by-phase run to 50 Founding Members.') +
      ramp +
      `<div class="grid">${cards}</div>` +
      footer('One phase at a time.'),
  );
}

// ---- Weekly Non-Negotiables — 16-week tracker ----
export function printNonNegotiables(chapters: PlaybookChapter[]): void {
  const items = (sectionOf(chapterOf(chapters, 'flow'), 'Weekly Non-Negotiables')?.fragments ?? []).map(
    (f) => f.body,
  );
  if (!items.length) return;
  const weeks = 16;
  const rows = items
    .map(
      (it, i) =>
        `<tr><td class="nn">${i + 1}. ${esc(it)}</td>${Array.from({ length: weeks })
          .map(() => '<td class="wk"></td>')
          .join('')}</tr>`,
    )
    .join('');
  const heads = Array.from({ length: weeks })
    .map((_, w) => `<th class="wh">${w + 1}</th>`)
    .join('');

  const css = `
    table { width:100%; border-collapse:collapse; margin-top:14px; }
    th,td { border:1px solid #E6DFCF; }
    thead th { background:#0C3D2D; color:#FBF6EC; font-size:10px; font-weight:700; padding:6px 4px; }
    thead th.lead { text-align:left; background:#0C3D2D; }
    .wh { width:22px; text-align:center; }
    td.nn { font-size:12px; padding:7px 10px; color:#16241E; }
    td.wk { width:22px; height:26px; }
    tbody tr:nth-child(odd) td.nn { background:#FFFDF8; }`;

  openPrintDoc(
    'Tee Box — Weekly Non-Negotiables Tracker',
    css,
    header('Owner Playbook', 'Weekly Non-Negotiables', 'Run every one, every week. Tick the box as you complete it — 16 weeks across.') +
      `<table><thead><tr><th class="lead">Non-Negotiable</th>${heads}</tr></thead><tbody>${rows}</tbody></table>` +
      footer('Every one, every week.'),
  );
}

// ---- Social Content Calendar (16 weeks) ----
export function printSocialCalendar(chapters: PlaybookChapter[]): void {
  const frs = sectionOf(chapterOf(chapters, 'social'), 'Weekly Posting Schedule')?.fragments ?? [];
  if (!frs.length) return;
  const weeks = frs
    .map((f) => {
      const d = f.data as unknown as ScheduleData;
      const items = (d.items ?? [])
        .map(
          (it) =>
            `<li><span class="box"></span><span><b>${esc(it.day)}</b> · <span class="fmt">${esc(
              it.format,
            )}</span><br>${esc(it.idea)}</span></li>`,
        )
        .join('');
      return `<div class="card wkcard"><div class="wkh"><span class="wkt">${esc(
        f.label,
      )}</span><span class="wkp">${esc(d.phase)}</span></div><ul>${items}</ul>${
        d.note ? `<p class="wknote">${esc(d.note)}</p>` : ''
      }</div>`;
    })
    .join('');

  const css = `
    .wkcard { margin-top:12px; }
    .wkh { display:flex; justify-content:space-between; align-items:baseline; gap:10px; margin-bottom:8px; }
    .wkt { font-size:15px; font-weight:800; color:#0C3D2D; } .wkp { font-size:11px; color:#6C756D; text-align:right; }
    .wkcard ul { list-style:none; margin:0; padding:0; } .wkcard li { display:flex; gap:10px; font-size:12.5px; line-height:1.5; padding:6px 0; border-bottom:1px dotted #E6DFCF; }
    .wkcard li:last-child { border-bottom:none; } .box { margin-top:2px; width:15px; height:15px; }
    .fmt { color:#7C6220; font-weight:600; }
    .wknote { margin:8px 0 0; font-size:11.5px; font-style:italic; color:#7C6220; border-left:2px solid #B58F2D; padding-left:8px; }`;

  openPrintDoc(
    'Tee Box — Social Content Calendar',
    css,
    header('Owner Playbook', 'Social Content Calendar', '16 weeks of posts, signing to opening. Check each one off as it goes live.') +
      weeks +
      footer('Consistency beats intensity.'),
  );
}

// ---- Persona battlecards ----
export function printPersonaCards(chapters: PlaybookChapter[]): void {
  const personas = chapterOf(chapters, 'personas');
  if (!personas) return;
  const frs = personas.sections.filter((s) => s.title !== 'Overview').flatMap((s) => s.fragments);
  const cards = frs
    .map((f) => {
      const d = f.data as unknown as PersonaData;
      const obj = (d.objections ?? [])
        .map(
          (o) =>
            `<div class="obj"><div class="say">${esc(o.obj)}</div><div class="resp">${esc(
              o.resp,
            )}</div></div>`,
        )
        .join('');
      return `<div class="card pcard">
        <div class="pgrp">${esc(d.group ?? '')}</div>
        <div class="pname">${esc(f.title)}</div>
        <div class="pk">Who</div><div class="pv">${esc(d.who)}</div>
        <div class="pk">Cares about</div><div class="pv">${esc(d.caresAbout)}</div>
        <div class="pk">Lead with</div><div class="pv lead">${esc(d.leadWith)}</div>
        ${obj ? `<div class="pk">Objections</div>${obj}` : ''}
      </div>`;
    })
    .join('');

  const css = `
    .grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-top:14px; }
    .pcard { break-inside:avoid; page-break-inside:avoid; }
    .pgrp { font-size:10px; letter-spacing:.14em; text-transform:uppercase; color:#7C6220; }
    .pname { font-size:16px; font-weight:800; color:#0C3D2D; margin:2px 0 8px; }
    .pk { font-size:10px; letter-spacing:.12em; text-transform:uppercase; color:#B58F2D; margin-top:8px; }
    .pv { font-size:12.5px; line-height:1.5; color:#16241E; } .pv.lead { background:#0C3D2D; color:#FBF6EC; border-radius:8px; padding:8px 10px; margin-top:3px; }
    .obj { margin-top:6px; } .say { font-size:12px; font-style:italic; color:#16241E; background:#F1E8D6; border-radius:6px; padding:5px 8px; }
    .resp { font-size:12px; color:#16241E; padding:5px 8px 0; }`;

  openPrintDoc(
    'Tee Box — Persona Battlecards',
    css,
    header('Owner Playbook', 'Pitching by Persona', 'Battlecards for the sales team — who they are, what to lead with, how to handle objections.') +
      `<div class="grid">${cards}</div>` +
      footer('Match the pitch to who is in front of you.'),
  );
}

// ---- Pitch + Objection one-pager ----
export function printPitch(chapters: PlaybookChapter[]): void {
  const offer = chapterOf(chapters, 'offer');
  const sec = sectionOf(offer, 'How to Pitch');
  if (!sec) return;
  const pitchFr = sec.fragments.find((f) => f.layout === 'pitch');
  const neverFr = sec.fragments.find((f) => f.layout === 'callout');
  const pairs = pitchFr ? (pitchFr.data as unknown as PitchData).pairs : [];
  const never = neverFr ? (neverFr.data as unknown as CalloutData) : undefined;
  const priceFr = sectionOf(offer, 'Pricing')?.fragments[0];
  const priceRows = priceFr ? (priceFr.data as unknown as KvListData).rows : [];

  const pitchHtml = pairs
    .map((p) => `<li><span class="box"></span><span><b>${esc(p.lead)}</b> ${esc(p.rest)}</span></li>`)
    .join('');
  const priceHtml = priceRows.length
    ? `<div class="price">${priceRows
        .map((r) => `<div class="pr"><span>${esc(r.label)}</span><b>${esc(r.value)}</b></div>`)
        .join('')}</div>`
    : '';
  const neverHtml = never
    ? `<div class="never"><div class="nt">${esc(never.title)}</div><div>${esc(never.text)}</div></div>`
    : '';

  const css = `
    ul.pitch { list-style:none; margin:14px 0 0; padding:0; } ul.pitch li { display:flex; gap:10px; font-size:13.5px; line-height:1.55; padding:7px 0; border-bottom:1px dotted #E6DFCF; }
    ul.pitch li b { color:#0C3D2D; } .box { margin-top:2px; }
    .price { display:flex; gap:12px; margin-top:12px; } .pr { flex:1; background:#FFFDF8; border:1px solid #E6DFCF; border-radius:10px; padding:12px 14px; display:flex; flex-direction:column; }
    .pr span { font-size:11px; text-transform:uppercase; letter-spacing:.1em; color:#6C756D; } .pr b { font-size:22px; color:#0C3D2D; }
    .never { margin-top:16px; border-left:3px solid #923430; background:rgba(146,52,48,.06); border-radius:8px; padding:12px 14px; }
    .nt { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#923430; margin-bottom:4px; }`;

  openPrintDoc(
    'Tee Box — Founding Member Pitch',
    css,
    header('Owner Playbook', 'How to Pitch It', 'The Founding Member offer — lead with the big number, handle it right, never undersell.') +
      `<ul class="pitch">${pitchHtml}</ul>` +
      (priceHtml ? `<h2>Founders Pricing</h2>${priceHtml}` : '') +
      neverHtml +
      footer('Lead with 35% off. Mean it.'),
  );
}

// ---- Grand Opening run-of-show ----
export function printRunOfShow(chapters: PlaybookChapter[]): void {
  const frs = sectionOf(chapterOf(chapters, 'growth'), 'Grand Opening Event')?.fragments ?? [];
  if (!frs.length) return;
  const blocks = frs
    .map((f) => {
      const d = f.data as Partial<KvListData & ListData>;
      let body = '';
      if (d.rows?.length) {
        body = `<div class="rows">${d.rows
          .map((r) => `<div class="row"><span class="rl">${esc(r.label)}</span><span>${esc(r.value)}</span></div>`)
          .join('')}</div>`;
      } else if (d.items?.length) {
        body = `<ul>${d.items.map((x) => `<li><span class="box"></span>${esc(x)}</li>`).join('')}</ul>`;
      } else {
        body = `<p>${esc(f.body)}</p>`;
      }
      return `<div class="card blk"><h3>${esc(f.label)}</h3>${body}</div>`;
    })
    .join('');

  const css = `
    .blk { margin-top:12px; } .blk h3 { margin:0 0 8px; font-size:14px; font-weight:700; color:#0C3D2D; }
    .rows .row { display:flex; gap:12px; padding:5px 0; font-size:12.5px; border-bottom:1px dotted #E6DFCF; }
    .rows .row:last-child { border-bottom:none; } .rl { min-width:150px; font-weight:600; color:#7C6220; }
    .blk ul { list-style:none; margin:0; padding:0; } .blk li { display:flex; gap:10px; font-size:12.5px; line-height:1.5; padding:5px 0; } .box { margin-top:1px; }
    .blk p { margin:0; font-size:12.5px; line-height:1.55; }`;

  openPrintDoc(
    'Tee Box — Grand Opening Run of Show',
    css,
    header('Owner Playbook', 'Grand Opening Event', 'Your day-of operating plan — timeline, checklist, run of show, invite list and logistics.') +
      blocks +
      footer('Everyone knows their role before doors open.'),
  );
}
